import React from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, MapPinIcon, GlobeIcon, PenToolIcon, ClockIcon, EyeIcon, BookOpenIcon, UsersIcon, ArrowRightIcon, LinkIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { getBookstoresForBook, bookstoreBookLinks } from '../../data/bookstoresData';
import { getPOVTypeById, getTenseTypeById } from '../../data/bookTags';
interface BookDetailsCardProps {
  book: any;
}
const getTypeColor = (type: string, darkMode: boolean) => {
  switch (type) {
    case 'brick-and-mortar':
      return darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-800';
    case 'online-only':
      return darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    case 'hybrid':
      return darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
    case 'pop-up':
      return darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800';
    case 'specialty':
      return darkMode ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-100 text-rose-800';
    default:
      return darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
  }
};
const getAvailabilityBadge = (type: string) => {
  switch (type) {
    case 'stocked':
      return {
        label: 'In Stock',
        color: 'bg-green-500'
      };
    case 'online-listing':
      return {
        label: 'Online',
        color: 'bg-blue-500'
      };
    case 'signed-copy':
      return {
        label: 'Signed',
        color: 'bg-purple-500'
      };
    case 'pre-order':
      return {
        label: 'Pre-order',
        color: 'bg-amber-500'
      };
    default:
      return {
        label: 'Available',
        color: 'bg-gray-500'
      };
  }
};
const formatType = (type: string) => {
  return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
export const BookDetailsCard = ({
  book
}: BookDetailsCardProps) => {
  const {
    darkMode
  } = useDarkMode();
  const availableBookstores = getBookstoresForBook(book.id);
  const bookstoreLinks = bookstoreBookLinks.filter(link => link.bookId === book.id);
  const povInfo = book.pov ? getPOVTypeById(book.pov) : null;
  const tenseInfo = book.tense ? getTenseTypeById(book.tense) : null;
  return <div data-id="element-7856" data-mp="4r7moto2q5vz">
      {/* Writing Style Card */}
      {(povInfo || tenseInfo) && <div className={`rounded-lg border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7857" data-mp="8rhumbvff6dh">
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7858" data-mp="ihb5fanqvhgo">
            <h3 className={`text-lg font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7859" data-mp="3h5qjb8utvd0d">
              <PenToolIcon className="h-5 w-5" data-id="element-7860" data-mp="3m3i83u06ej0c" />
              Writing Style
            </h3>
          </div>
          <div className="px-6 py-4" data-id="element-7861" data-mp="39n92ehkmgvxd">
            <div className="flex flex-wrap gap-3" data-id="element-7862" data-mp="39oahcx4re2kd">
              {povInfo && <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`} data-id="element-7863" data-mp="34nmn82l8h5lx">
                  <EyeIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} data-id="element-7864" data-mp="2vnr9bq0amwi7" />
                  <div data-id="element-7865" data-mp="2quogxl795t3l">
                    <div className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7866" data-mp="2lxeqq3c1hdep">
                      {povInfo.name}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7867" data-mp="2cx9ixs2xod00">
                      {povInfo.description}
                    </div>
                  </div>
                </div>}
              {tenseInfo && <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`} data-id="element-7868" data-mp="fwlt91lvi377">
                  <ClockIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} data-id="element-7869" data-mp="738vnmcv6des" />
                  <div data-id="element-7870" data-mp="3oei7kmnlmc3j">
                    <div className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7871" data-mp="3tbrxs4itarsf">
                      {tenseInfo.name}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7872" data-mp="3kbln0qei05wy">
                      {tenseInfo.description}
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>}

      {/* Anthology Contributors */}
      {book.isAnthology && book.contributors && book.contributors.length > 0 && <div className={`rounded-lg border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7873" data-mp="21fft2oqzjflr">
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7874" data-mp="1wnfx17e1d6ug">
              <h3 className={`text-lg font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7875" data-mp="26eixq831zjt7">
                <UsersIcon className="h-5 w-5" data-id="element-7876" data-mp="2kds2mblt6rhv" />
                Contributing Authors ({book.contributors.length})
              </h3>
            </div>
            <div className="px-6 py-4" data-id="element-7877" data-mp="q00wvn6lorrp">
              <div className="space-y-3" data-id="element-7878" data-mp="q127xrnm9e9f">
                {book.contributors.map((contributor: any, idx: number) => <Link key={idx} to={`/author/${contributor.authorSlug}`} className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 group ${darkMode ? 'border-gray-700 hover:border-indigo-500 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'}`} data-id="element-7879" data-mp="m0ia0lv7y6m8" data-mpi={idx}>
                    <div className="flex items-center gap-3 min-w-0" data-id="element-7880" data-mp="30210796d1395">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-600'}`} data-id="element-7881" data-mp="341jn2ahqroem">
                        {contributor.authorName.charAt(0)}
                      </div>
                      <div className="min-w-0" data-id="element-7882" data-mp="3it1z5x7hcysp">
                        <div className={`font-medium truncate transition-colors ${darkMode ? 'text-gray-200 group-hover:text-indigo-400' : 'text-gray-900 group-hover:text-indigo-600'}`} data-id="element-7883" data-mp="3nrbv4a1143fd">
                          {contributor.authorName}
                        </div>
                        <div className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7884" data-mp="1c1dv5fkuo3yd">
                          "{contributor.storyTitle}"
                          {contributor.pages && <span className="ml-1" data-id="element-7885" data-mp="1zst0lh7xb1du">· {contributor.pages}p</span>}
                        </div>
                      </div>
                    </div>
                    <ArrowRightIcon className={`h-4 w-4 flex-shrink-0 transition-colors ${darkMode ? 'text-gray-500 group-hover:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-500'}`} data-id="element-7886" data-mp="1q26ohwzf0szw" />
                  </Link>)}
              </div>
            </div>
          </div>}

      {/* Series Crossovers */}
      {book.crossoverSeries && book.crossoverSeries.length > 0 && <div className={`rounded-lg border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7887" data-mp="1m36hj8b41x0n">
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7888" data-mp="1d4kylc1q8fzc">
            <h3 className={`text-lg font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7889" data-mp="1h6fzwgjnqtd4">
              <LinkIcon className="h-5 w-5" data-id="element-7890" data-mp="29ubu9elr896q" />
              Series Crossovers
            </h3>
          </div>
          <div className="px-6 py-4 space-y-2" data-id="element-7891" data-mp="1vusyzme1ksbu">
            {book.crossoverSeries.map((crossover: any, idx: number) => <Link key={idx} to={`/series/${crossover.seriesSlug}`} className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 group ${darkMode ? 'border-gray-700 hover:border-indigo-500 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'}`} data-id="element-7892" data-mp="20sugw3zqellq" data-mpi={idx}>
                <div className="flex items-center gap-3" data-id="element-7893" data-mp="cw4qojtrnrp1">
                  <BookOpenIcon className={`h-5 w-5 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} data-id="element-7894" data-mp="cvl7kzsdqx9f" />
                  <div data-id="element-7895" data-mp="lw9pw6ot3js4">
                    <div className={`font-medium transition-colors ${darkMode ? 'text-gray-200 group-hover:text-indigo-400' : 'text-gray-900 group-hover:text-indigo-600'}`} data-id="element-7896" data-mp="lx2oulhix4zp">
                      {crossover.seriesName}
                    </div>
                    {crossover.readingOrder && <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7897" data-mp="361qnzo89dh7">
                        Read this book #{crossover.readingOrder} in combined
                        order
                      </div>}
                  </div>
                </div>
                <ArrowRightIcon className={`h-4 w-4 flex-shrink-0 transition-colors ${darkMode ? 'text-gray-500 group-hover:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-500'}`} data-id="element-7898" data-mp="2mqm2jdh7bjra" />
              </Link>)}
          </div>
        </div>}

      {/* Book Details */}
      <div className={`rounded-lg border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7899" data-mp="27u4vuzjyitb2">
        <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7900" data-mp="1z1jhrwr9task">
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7901" data-mp="1u41ccgcgpdpb">
            Book Details
          </h3>
        </div>
        <div className="px-6 py-4" data-id="element-7902" data-mp="1k4k8m4grt9vz">
          <dl className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`} data-id="element-7903" data-mp="1k3iprdtijj3p">
            {book.consentFramework && <div className="py-3 flex justify-between" data-id="element-7904" data-mp="3rqu4iq7j0vwh">
                <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7905" data-mp="3rqaojf1fshl3">
                  Consent Framework
                </dt>
                <dd className={`text-sm capitalize ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-7906" data-mp="4mnqh9ygltit">
                  {book.consentFramework}
                </dd>
              </div>}
            <div className="py-3 flex justify-between" data-id="element-7907" data-mp="3d1nxwh6d03ek">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7908" data-mp="3d27enuk9wlr0">
                {book.coming_soon ? 'Expected Release' : 'Published'}
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7909" data-mp="3mtk98txghq0q">
                {book.publishDate}
              </dd>
            </div>
            <div className="py-3 flex justify-between" data-id="element-7910" data-mp="2u9bzg5ittm9p">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7911" data-mp="2m9p2ntwmgeoz">
                Publisher
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7912" data-mp="2w00lemkyssfx">
                {book.publisher}
              </dd>
            </div>
            <div className="py-3 flex justify-between" data-id="element-7913" data-mp="23bmlg1abfm1q">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7914" data-mp="23c4pvl74t6kq">
                Pages
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7915" data-mp="2da2c635fktap">
                {book.pages || 'TBA'}
              </dd>
            </div>
            <div className="py-3 flex justify-between" data-id="element-7916" data-mp="3oo65r883wff8">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7917" data-mp="3or20yoskkpzz">
                Language
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7918" data-mp="15gjzvnab0wr3">
                {book.language}
              </dd>
            </div>
            <div className="py-3 flex justify-between" data-id="element-7919" data-mp="wiqeh7e2vnip">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7920" data-mp="11g8eg8a4asek">
                ISBN
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7921" data-mp="1p873rw6eioap">
                {book.isbn}
              </dd>
            </div>
            <div className="py-3 flex justify-between" data-id="element-7922" data-mp="1fg121uqcksib">
              <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7923" data-mp="1keuk80gkx7ot">
                Age Rating
              </dt>
              <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7924" data-mp="2cvopb9z2oq58">
                {book.ageRating}
              </dd>
            </div>
            {/* POV row */}
            {povInfo && <div className="py-3 flex justify-between" data-id="element-7925" data-mp="j9zntpkb64ke">
                <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7926" data-mp="f8w7l1ycm6in">
                  POV
                </dt>
                <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7927" data-mp="acflseemg48q">
                  {povInfo.name}
                </dd>
              </div>}
            {/* Tense row */}
            {tenseInfo && <div className="py-3 flex justify-between" data-id="element-7928" data-mp="y6guw4lgsx15">
                <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7929" data-mp="t9gofskpds9i">
                  Tense
                </dt>
                <dd className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7930" data-mp="1lxw5ekneaknh">
                  {tenseInfo.name}
                </dd>
              </div>}
          </dl>
        </div>
      </div>

      {/* Where to Buy */}
      {(book.externalLinks?.length > 0 || availableBookstores.length > 0) && <div className={`rounded-lg border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7931" data-mp="235nbaix5e4ad">
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7932" data-mp="2c8emqiq0x020">
            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7933" data-mp="2h553775qpxny">
              {book.coming_soon ? 'Pre-Order' : 'Where to Buy'}
            </h3>
          </div>
          <div className="px-6 py-4 space-y-2" data-id="element-7934" data-mp="2qylx0b5vncrm">
            {/* External retailer links */}
            {book.externalLinks?.map((link: any, idx: number) => <a key={`ext-${idx}`} href={link.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 group ${darkMode ? 'border-gray-700 hover:border-indigo-500 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'}`} data-id="element-7935" data-mp="2zy8ysy2iqkl7">
                <div className="flex items-center gap-3" data-id="element-7936" data-mp="14ry7che8d5ur">
                  <span className="text-lg" data-id="element-7937" data-mp="1jhw1rf2r6rse">
                    {link.site === 'Amazon' ? '🛒' : link.site === 'Apple Books' ? '🍎' : link.site === 'Kobo' ? '📱' : '🔗'}
                  </span>
                  <span className={`font-medium transition-colors ${darkMode ? 'text-gray-200 group-hover:text-indigo-400' : 'text-gray-900 group-hover:text-indigo-600'}`} data-id="element-7938" data-mp="1nifw6avxzhau">
                    {link.site}
                  </span>
                </div>
                <span className={`text-sm transition-colors ${darkMode ? 'text-gray-500 group-hover:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-500'}`} data-id="element-7939" data-mp="24umsqnkdxt4u">
                  Visit →
                </span>
              </a>)}

            {/* Bookstore links */}
            {availableBookstores.map(store => {
          const linkInfo = bookstoreLinks.find(l => l.bookstoreId === store.id);
          const badge = linkInfo ? getAvailabilityBadge(linkInfo.availabilityType) : null;
          return <Link key={store.id} to={`/bookstores/${store.slug}`} className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 group ${darkMode ? 'border-gray-700 hover:border-indigo-500 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'}`} data-id="element-7940" data-mp="1h40kav6gy6ub">
                  <img src={store.logo} alt={store.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-7941" data-mp="1zasqlg1qrqt0" />
                  <div className="flex-1 min-w-0" data-id="element-7942" data-mp="1pgsbclcqx56k">
                    <span className={`font-medium transition-colors truncate block ${darkMode ? 'text-gray-200 group-hover:text-indigo-400' : 'text-gray-900 group-hover:text-indigo-600'}`} data-id="element-7943" data-mp="1kj0h3komp6z7">
                      {store.name}
                    </span>
                    <div className={`flex items-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7944" data-mp="16lbwmdyh41uo">
                      {store.type === 'online-only' ? <>
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-7945" data-mp="30vxros5sv28n" />
                          <span data-id="element-7946" data-mp="2w0if03jj3cdt">Online · {store.country}</span>
                        </> : <>
                          <MapPinIcon className="h-3 w-3 mr-1" data-id="element-7947" data-mp="38ht88jxabefe" />
                          <span data-id="element-7948" data-mp="38g9nag0oof7a">
                            {store.city ? `${store.city}, ` : ''}
                            {store.country}
                          </span>
                        </>}
                    </div>
                  </div>
                  {badge && <span className={`${badge.color} text-white text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0`} data-id="element-7949" data-mp="2lj6eafdx99kw">
                      {badge.label}
                    </span>}
                </Link>;
        })}
          </div>
        </div>}
    </div>;
};
export default BookDetailsCard;